import { memo, useCallback, useState } from "react";
import { useTranslate } from "helpers";
import { updateFleetVehicleRegistrationAPI } from "services/api/fleet/vehicles/api";
import { useHandleTextFieldChange, useHandleDateChange } from "services/forms/hooks";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { VehicleRegistrationModalProps, VehicleRegistrationFormValues } from "./types";
import { FLEET_VEHICLE_REGISTRATION_FORM_SCHEMA } from "./validation";

const initialFormValues: VehicleRegistrationFormValues = {
    registrationPlate: "",
    registrationExpiration: null,
};

const RegistrationEditModal: React.FC<VehicleRegistrationModalProps> = (props) => {
    const { show, onClose, onSuccess, vehicleId } = props;

    const { t } = useTranslate();
    const [isLoading, setIsLoading] = useState(false);
    const [formValues, setFormValues] = useState<VehicleRegistrationFormValues>(initialFormValues);
    const [errors, setErrors] = useState<Errors>({});

    const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
    const handleDateChange = useHandleDateChange(setFormValues, formValues);

    const handleSubmit = async () => {
        const isValid = validateYup(formValues, FLEET_VEHICLE_REGISTRATION_FORM_SCHEMA, setErrors);
        if (!isValid) {
            return;
        }

        setIsLoading(true);
        try {
            await updateFleetVehicleRegistrationAPI(
                vehicleId,
                formValues.registrationPlate,
                formValues.registrationExpiration,
                t
            );
            onSuccess(formValues.registrationPlate, formValues.registrationExpiration);
            handleClose();
        } catch (error) {
            console.error("Registration update failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = useCallback(() => {
        setFormValues(initialFormValues);
        setErrors({});
        onClose();
    }, [onClose]);

    return (
        <Modal show={show} onHide={handleClose} size="small" position="centered">
            <ModalHeader
                icon="/media/images/add-icon.svg"
                title={t("admin.fleet.vehicle.detail.info.registration")}
                onHide={handleClose}
            />
            <ModalBody>
                <div className="p-4">
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <TextField
                                label={t("admin.fleet.vehicle.detail.info.registrationplate")}
                                type="text"
                                name="registrationPlate"
                                value={formValues.registrationPlate}
                                onChange={handleTextFieldChange}
                                error={errors.registrationPlate}
                                required
                                disableDebounce
                                width="full"
                            />
                        </div>
                        <div className="flex-1">
                            <DatePicker
                                label={t("admin.fleet.vehicle.detail.info.registrationexpiration")}
                                name="registrationExpiration"
                                value={formValues.registrationExpiration}
                                onChange={handleDateChange}
                                error={errors.registrationExpiration}
                                required
                                width="full"
                            />
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className="px-4 py-3 flex justify-end gap-3">
                    <Button variant="outlined" onClick={handleClose}>
                        {t("admin.cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isLoading}
                    >
                        {t("admin.save")}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default memo(RegistrationEditModal);
